import { useUpdateMeterReaderAccess, useGetMeterReaderDetail } from "@features/mx/settings/hooks";
import { Button } from "@shared/ui/button";
import { Card } from "@shared/ui/card";
import {
  ClipboardList,
  Edit,
  MessageSquare,
  UserX,
  Zap,
} from "lucide-react";
import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DisableAccountConfirmation } from "./DisableAccountConfirmation";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { CommunicationDialog } from "@features/cx/shared/components/CommunicationDialog";
import { useChannelTemplates, useChannelType, useSendCommunication } from "@features/cx/consumerManagement/hooks";

export const QuickActions = ({data}: {data: any}) => {
  
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const id = searchParams.get("view");
  const remoteUtilityId = useRemoteUtilityId();
  const { mutateAsync: updateMeterReaderAccess, isPending: isUpdatingAccess } = useUpdateMeterReaderAccess();
  const { data: readerDetail } = useGetMeterReaderDetail(remoteUtilityId, id);

  const [openSendComm, setOpenSendComm] = useState(false);
  const [openDisableDialog, setOpenDisableDialog] = useState(false);

  const isUserAccess = data?.isUserAccess ?? readerDetail?.isUserAccess;
  const actionType = isUserAccess ? "disable" : "enable";

  const handleToggleAccess = async () => {
    if (id) {
      await updateMeterReaderAccess({
        id,
        payload: { app_account_access: !isUserAccess },
      });
      setOpenDisableDialog(false);
    }
  };


  const quickActions = [
    {
      label: "Send Communication",
      icon: MessageSquare,
      onClick: () => setOpenSendComm(true),
      variant: "secondary" as const,
      className:
        "w-full justify-start gap-3 bg-blue-100 text-blue-900 hover:bg-blue-200 border-none h-10",
    },
    {
      label: "Edit Profile",
      icon: Edit,
      onClick: () => {
        if (id) {
          setSearchParams({ edit: id });
        }
      },
      variant: "outline" as const,
      className:
        "w-full justify-start gap-3 h-10 text-gray-700 border-gray-200",
    },
    {
      label: "Assign Route",
      icon: ClipboardList,
      onClick: () => navigate("/mx/meter-validation"),
      variant: "outline" as const,
      className:
        "w-full justify-start gap-3 h-10 text-gray-700 border-gray-200",
    },
    {
      label: isUserAccess ? "Disable App Account" : "Enable App Account",
      icon: UserX,
      onClick: () => setOpenDisableDialog(true),
      variant: "outline" as const,
      className: `w-full justify-start gap-3 h-10 ${
        isUserAccess
          ? "text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 hover:border-red-300"
          : "text-green-600 border-green-200 hover:bg-green-50 hover:text-green-700 hover:border-green-300"
      }`,
      withSeparator: true,
    },
  ];

  return (
    <Card className="p-4 h-full">
      <div className="flex items-center gap-2 mb-6">
        <Zap className="h-5 w-5" />
        <h3 className="font-semibold text-lg">Quick Actions</h3>
      </div>

      <div className="space-y-4">
        {quickActions.map((action, index) => {
          const ButtonComponent = (
            <Button
              key={action.label}
              className={action.className}
              variant={action.variant}
              onClick={action.onClick}
            >
              <action.icon className="h-5 w-5" />
              <span className="font-medium">{action.label}</span>
            </Button>
          );

          if (action.withSeparator) {
            return (
              <div key={action.label} className="pt-4 border-t border-gray-100">
                {ButtonComponent}
              </div>
            );
          }

          return ButtonComponent;
        })}
      </div>

       <CommunicationDialog
        isOpen={openSendComm}
        onClose={() => setOpenSendComm(false)}
        consumerId={id || undefined}
        remoteUtilityId={Number(remoteUtilityId)}
        email={readerDetail?.email}
        phone={readerDetail?.contact_number}
        useChannelType={useChannelType}
        useChannelTemplates={useChannelTemplates}
        useSendCommunication={useSendCommunication}
        communicationType="meterReader"
      />

      <DisableAccountConfirmation
        isOpen={openDisableDialog}
        onClose={() => setOpenDisableDialog(false)}
        onConfirm={handleToggleAccess}
        mrName={readerDetail?.name}
        isLoading={isUpdatingAccess}
        actionType={actionType}
      />
    </Card>
  );
};
